import { useCallback, useEffect, useMemo, useReducer } from "react";
import type { DashboardSnapshot } from "@/domain/model/Dashboard";
import type { AccountUserRole } from "@/domain/model/AccountSeatOverview";
import type { GetDashboardSnapshot } from "@/domain/usecase/GetDashboardSnapshot";
import type { InviteAccountUser } from "@/domain/usecase/InviteAccountUser";

type DashboardActionName = "invite";

type InviteDialogState = {
  open: boolean;
  email: string;
  role: AccountUserRole;
};

type UiState = {
  status: "loading" | "ready" | "error";
  snapshot?: DashboardSnapshot;
  loadError?: string;
  actionError?: string;
  successMessage?: string;
  activeAction?: DashboardActionName;
  inviteDialog: InviteDialogState;
};

type Action =
  | { type: "LOAD" }
  | { type: "SUCCESS"; snapshot: DashboardSnapshot }
  | { type: "ERROR"; message: string }
  | { type: "OPEN_INVITE_DIALOG" }
  | { type: "CLOSE_INVITE_DIALOG" }
  | { type: "SET_INVITE_EMAIL"; value: string }
  | { type: "SET_INVITE_ROLE"; value: AccountUserRole }
  | { type: "ACTION_STARTED"; action: DashboardActionName }
  | { type: "ACTION_SUCCESS"; message: string }
  | { type: "ACTION_ERROR"; message: string }
  | { type: "CLEAR_MESSAGES" };

const initialState: UiState = {
  status: "loading",
  inviteDialog: {
    open: false,
    email: "",
    role: "WORKER",
  },
};

function reducer(state: UiState, action: Action): UiState {
  switch (action.type) {
    case "LOAD":
      return {
        ...state,
        status: "loading",
        loadError: undefined,
      };

    case "SUCCESS":
      return {
        ...state,
        status: "ready",
        snapshot: action.snapshot,
        loadError: undefined,
      };

    case "ERROR":
      return {
        ...state,
        status: "error",
        loadError: action.message,
      };

    case "OPEN_INVITE_DIALOG":
      return {
        ...state,
        inviteDialog: {
          ...state.inviteDialog,
          open: true,
        },
        actionError: undefined,
        successMessage: undefined,
      };

    case "CLOSE_INVITE_DIALOG":
      return {
        ...state,
        inviteDialog: {
          open: false,
          email: "",
          role: "WORKER",
        },
        actionError: undefined,
      };

    case "SET_INVITE_EMAIL":
      return {
        ...state,
        inviteDialog: {
          ...state.inviteDialog,
          email: action.value,
        },
      };

    case "SET_INVITE_ROLE":
      return {
        ...state,
        inviteDialog: {
          ...state.inviteDialog,
          role: action.value,
        },
      };

    case "ACTION_STARTED":
      return {
        ...state,
        activeAction: action.action,
        actionError: undefined,
        successMessage: undefined,
      };

    case "ACTION_SUCCESS":
      return {
        ...state,
        activeAction: undefined,
        actionError: undefined,
        successMessage: action.message,
        inviteDialog: {
          open: false,
          email: "",
          role: "WORKER",
        },
      };

    case "ACTION_ERROR":
      return {
        ...state,
        activeAction: undefined,
        actionError: action.message,
      };

    case "CLEAR_MESSAGES":
      return {
        ...state,
        actionError: undefined,
        successMessage: undefined,
      };

    default:
      return state;
  }
}

interface UseDashboardViewModelParams {
  getDashboardSnapshot: GetDashboardSnapshot;
  inviteAccountUser: InviteAccountUser;
}

export function useDashboardViewModel({
  getDashboardSnapshot,
  inviteAccountUser
}: UseDashboardViewModelParams) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const load = useCallback(
    async (signal?: AbortSignal) => {
      dispatch({ type: "LOAD" });

      try {
        const snapshot = await getDashboardSnapshot.execute(signal);
        dispatch({ type: "SUCCESS", snapshot });
      } catch (error: unknown) {
        if (isAbortError(error)) return;

        dispatch({
          type: "ERROR",
          message: getDashboardErrorMessage(error),
        });
      }
    },
    [getDashboardSnapshot],
  );

  useEffect(() => {
    const controller = new AbortController();

    void load(controller.signal);

    return () => {
      controller.abort();
    };
  }, [load]);

  const openInviteDialog = useCallback(() => {
    dispatch({ type: "OPEN_INVITE_DIALOG" });
  }, []);

  const closeInviteDialog = useCallback(() => {
    dispatch({ type: "CLOSE_INVITE_DIALOG" });
  }, []);

  const setInviteEmail = useCallback((value: string) => {
    dispatch({ type: "SET_INVITE_EMAIL", value });
  }, []);

  const setInviteRole = useCallback((value: AccountUserRole) => {
    dispatch({ type: "SET_INVITE_ROLE", value });
  }, []);

  const clearMessages = useCallback(() => {
    dispatch({ type: "CLEAR_MESSAGES" });
  }, []);

  const submitInvite = useCallback(async () => {
    const email = state.inviteDialog.email.trim();
    const role = state.inviteDialog.role;

    if (!email) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Bitte eine E-Mail-Adresse eingeben.",
      });
      return;
    }

    if (!state.snapshot) {
      dispatch({
        type: "ACTION_ERROR",
        message: "Dashboard-Daten sind noch nicht geladen.",
      });
      return;
    }

    if (!hasFreeSeat(state.snapshot)) {
      dispatch({
        type: "ACTION_ERROR",
        message:
          "Keine freien App-Zugänge verfügbar. Bitte zuerst die Sitzanzahl erhöhen.",
      });
      return;
    }

    const controller = new AbortController();

    dispatch({ type: "ACTION_STARTED", action: "invite" });

    try {
      await inviteAccountUser.execute(
        {
          email,
          role,
        },
        controller.signal,
      );

      await load(controller.signal);

      dispatch({
        type: "ACTION_SUCCESS",
        message: "Einladung wurde erstellt.",
      });
    } catch (error: unknown) {
      if (isAbortError(error)) return;

      dispatch({
        type: "ACTION_ERROR",
        message: getInviteErrorMessage(error),
      });
    }
  }, [
    inviteAccountUser,
    load,
    state.inviteDialog.email,
    state.inviteDialog.role,
    state.snapshot,
  ]);

  return useMemo(
    () => ({
      state: {
        loading: state.status === "loading",
        error: state.status === "error" ? state.loadError : undefined,
        snapshot: state.snapshot,

        actionError: state.actionError,
        successMessage: state.successMessage,
        activeAction: state.activeAction,

        inviteDialog: state.inviteDialog,
      },
      derived: {
        isInviteBusy: state.activeAction === "invite",
        hasFreeSeat: state.snapshot ? hasFreeSeat(state.snapshot) : false,
      },
      intents: {
        reload: () => load(),

        openInviteDialog,
        closeInviteDialog,
        setInviteEmail,
        setInviteRole,
        submitInvite,

        clearMessages,
      },
    }),
    [
      state,
      load,
      openInviteDialog,
      closeInviteDialog,
      setInviteEmail,
      setInviteRole,
      submitInvite,
      clearMessages,
    ],
  );
}

function hasFreeSeat(snapshot: DashboardSnapshot): boolean {
  const seatUsage = snapshot.setup.seatUsage;

  if (!seatUsage) {
    return true;
  }

  return seatUsage.usedSeats < seatUsage.purchasedSeats;
}

function getDashboardErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return "Dashboard konnte nicht geladen werden.";
}

function getInviteErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }

  return "Einladung konnte nicht erstellt werden.";
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === "AbortError";
}
